import axiosInstance from ".";
import { LOADER_TYPE } from "../helpers";
import { setIsLoader, setIsTableLoader } from "../redux/features/commonSlice";
import { store } from "../redux/store";
import { errorAlert, successAlert } from "../utils";

interface axiosMethodType {
  baseUrl?: string;
  path: string;
  headers?: any;
  data?: any;
}

interface responseType {
  data?: {
    message: string;
    statusCode: number;
  };
}

/**
 * @function handleOnSetLoader: Handle loaders
 * @param {Boolean} isLoading Loader On/Off
 * @param {Object} payload API request
 */
function handleOnSetLoader(isLoading: boolean, payload: any = {}) {
  const { isLoading: loading = true, loaderType } = payload;
  if (loading) {
    if (loaderType === LOADER_TYPE.TABLE) {
      store.dispatch(setIsTableLoader(isLoading));
    } else {
      store.dispatch(setIsLoader(isLoading));
    }
  }
}

/**
 * @function handleApiThen: Handle API success response
 * @param {Object} response API response
 * @param {Object} payload API request
 */
function handleApiThen(response: responseType, payload: any) {
  handleOnSetLoader(false, payload);
  if (payload.callback) {
    payload.callback(response.data || true);
  }
  if (payload.notify && response.data?.message) {
    successAlert(response.data.message);
  }
}

/**
 * @function handleApiCatch: Handle API failure response
 * @param {Object} error API error response
 * @param {Object} data API request data
 */
function handleApiCatch(error: any, data?: any) {
  handleOnSetLoader(false, data);
  if ([404, 400, 409].includes(error.response?.data?.statusCode)) {
    errorAlert(error.response.data.message);
  } else if (error.message) {
    errorAlert(error.message);
  }
}

/**
 * @function getApiRequest: Set API request
 * @param {Object} data API request data
 */
function getApiRequest(data: any) {
  const req = Object.assign({}, data);
  delete req.notify;
  delete req.callback;
  delete req.loaderType;
  delete req.isLoading;

  return req;
}

/**
 * @function getApiUrl: Set API request
 * @param {Object} data API request configuration
 */
function getApiUrl(data: { baseUrl: String; path: String }) {
  return `${data.baseUrl}/${data.path}`;
}

/**
 * @function To call get API's
 * @param param0 get data
 * @returns api response on success or error on fail
 */
export const getDataApi = ({
  baseUrl = "v1",
  path = "no-path-provided",
  data = {},
}: axiosMethodType) => {
  try {
    handleOnSetLoader(true, data);
    return new Promise((resolve, reject) => {
      axiosInstance
        .get(getApiUrl({ path, baseUrl }), {
          params: getApiRequest(data),
        })
        .then((response: any) => {
          handleApiThen(response, data);
          resolve(response.data);
        })
        .catch((error: any) => {
          handleApiCatch(error, data);
          reject(error);
        });
    });
  } catch (error: any) {
    handleApiCatch(error, data);
    return error.message;
  }
};

export const getDataApiForSearch = ({
  baseUrl = "v1",
  path = "no-path-provided",
  data = {},
}: axiosMethodType) => {
  try {
    // handleOnSetLoader(true, data);
    return new Promise((resolve, reject) => {
      axiosInstance
        .get(getApiUrl({ path, baseUrl }), {
          params: getApiRequest(data),
        })
        .then((response: any) => {
          handleApiThen(response, data);
          resolve(response.data);
        })
        .catch((error: any) => {
          handleApiCatch(error, data);
          reject(error);
        });
    });
  } catch (error: any) {
    handleApiCatch(error, data);
    return error.message;
  }
};

/**
 * @function To call post API's
 * @param param0 post data
 * @returns api response on success or error on fail
 */
export const postDataApi = ({
  baseUrl = "v1",
  path = "no-path-provided",
  data = {},
}: axiosMethodType) => {
  try {
    handleOnSetLoader(true, data);
    return new Promise((resolve, reject) => {
      axiosInstance
        .post(getApiUrl({ path, baseUrl }), getApiRequest(data))
        .then((response: any) => {
          handleApiThen(response, data);
          resolve(response.data);
        })
        .catch((error: any) => {
          handleApiCatch(error, data);
          reject(error);
        });
    });
  } catch (error: any) {
    handleApiCatch(error, data);
    return error.message;
  }
};

export const postDataApiForSearch = ({
  baseUrl = "v1",
  path = "no-path-provided",
  data = {},
}: axiosMethodType) => {
  try {
    return new Promise((resolve, reject) => {
      axiosInstance
        .post(getApiUrl({ path, baseUrl }), getApiRequest(data))
        .then((response: any) => {
          handleApiThen(response, data);
          resolve(response.data);
        })
        .catch((error: any) => {
          handleApiCatch(error, data);
          reject(error);
        });
    });
  } catch (error: any) {
    handleApiCatch(error, data);
    return error.message;
  }
};

/**
 * @function To call put API's
 * @param param0 put data
 * @returns api response on success or error on fail
 */
export const putDataApi = ({
  baseUrl = "v1",
  path = "no-path-provided",
  data = {},
}: axiosMethodType) => {
  try {
    handleOnSetLoader(true, data);
    return new Promise((resolve, reject) => {
      axiosInstance
        .put(getApiUrl({ path, baseUrl }), getApiRequest(data))
        .then((response: any) => {
          handleApiThen(response, data);
          resolve(response.data);
        })
        .catch((error: any) => {
          handleApiCatch(error, data);
          reject(error);
        });
    });
  } catch (error: any) {
    handleApiCatch(error, data);
    return error.message;
  }
};

/**
 * @function To call delete API's
 * @param param0 delete data
 * @returns api response on success or error on fail
 */
export const deleteDataApi = ({
  baseUrl = "v1",
  path = "no-path-provided",
  data = {},
}: axiosMethodType) => {
  try {
    handleOnSetLoader(true, data);
    return new Promise((resolve, reject) => {
      axiosInstance
        .delete(getApiUrl({ path, baseUrl }), getApiRequest(data))
        .then((response: any) => {
          handleApiThen(response, data);
          resolve(response.data);
        })
        .catch((error: any) => {
          handleApiCatch(error, data);
          reject(error);
        });
    });
  } catch (error: any) {
    handleApiCatch(error, data);
    return error.message;
  }
};
